import { html, nothing } from "lit";
import { t } from "../i18n.ts";
import { renderKnowledgeSources } from "./knowledge-sources.ts";
import type { KnowledgeSourcesViewProps } from "./knowledge-sources.ts";
import type { KnowledgeSourceUI } from "../controllers/knowledge-sources.ts";

export type MemoryFileEntry = {
  name: string;
  path: string;
  size: number;
  modifiedAt: number;
};

export type LearningProps = {
  connected: boolean;
  loading: boolean;
  error: string | null;
  memory: string | null;
  soul: string | null;
  identity: string | null;
  user: string | null;
  emotionalState: unknown | null;
  userProfile: unknown | null;
  tieredStats: unknown | null;
  activeSection: string;
  initializing: string | null;
  memoryFiles: MemoryFileEntry[];
  memoryFilesLoading: boolean;
  selectedMemoryFile: string | null;
  selectedMemoryContent: string | null;
  onSectionChange: (section: string) => void;
  onRefresh: () => void;
  onInitModule: (moduleKey: string) => void;
  onInitAll: () => void;
  onLoadMemoryFiles: () => void;
  onSelectMemoryFile: (filePath: string) => void;
  // Knowledge Sources integration
  knowledgeSubTab: "sources" | "collected";
  knowledgeSourcesLoading: boolean;
  knowledgeSourcesError: string | null;
  knowledgeSources: KnowledgeSourceUI[];
  knowledgeSourceEditing: KnowledgeSourceUI | null;
  knowledgeSourceFormOpen: boolean;
  knowledgeSourceCollecting: string | null;
  onKnowledgeSubTabChange: (tab: "sources" | "collected") => void;
  onKnowledgeSourceRefresh: () => void;
  onKnowledgeSourceAdd: () => void;
  onKnowledgeSourceEdit: (source: KnowledgeSourceUI) => void;
  onKnowledgeSourceDelete: (id: string) => void;
  onKnowledgeSourceCollect: (id: string) => void;
  onKnowledgeSourceToggle: (id: string, enabled: boolean) => void;
  onKnowledgeSourceFormClose: () => void;
  onKnowledgeSourceFormSave: (data: Partial<KnowledgeSourceUI>) => void;
};

type EmotionVector = {
  valence?: number;
  arousal?: number;
  dominance?: number;
  attachment?: number;
  desire?: number;
  aggression?: number;
  confusion?: number;
};

/**
 * Normalized emotional state — the controller maps both the emotional-core
 * extension format and the AI-generated learning module format into this shape.
 */
type EmotionalState = {
  currentEmotion?: string;
  emotionVector?: EmotionVector;
  activeEmotions?: string[];
  mood?: { valence?: number; stability?: number; energy?: number; warmth?: number };
  loyalty?: {
    loyaltyLevel?: number;
    bondStrength?: number;
    protectiveInstinct?: number;
    devotionStyle?: string;
  };
  memories?: Array<{ emotion?: string; summary?: string; timestamp?: string }>;
};

type UserProfile = {
  communicationStyle?: string;
  expertiseLevel?: string;
  responsePreference?: string;
  mentalState?: {
    emotion?: string;
    cognitiveLoad?: string;
    engagement?: string;
    frustrationSignals?: string[];
  };
  interactionStats?: {
    totalMessages?: number;
    questions?: number;
    commands?: number;
    codeSnippets?: number;
  };
  topInterests?: string[];
  preferredLanguages?: string[];
};

type TieredStats = {
  shortTermCount: number;
  longTermCount: number;
  entityCount: number;
  totalMemories: number;
  entities: Record<string, unknown>;
  shortTerm: unknown[];
  longTerm: unknown[];
};

const SECTIONS = ["overview", "memory", "emotions", "personality", "user", "knowledge"] as const;

function sectionIcon(section: string): string {
  switch (section) {
    case "overview":
      return "\u{1F4CA}";
    case "memory":
      return "\u{1F9E0}";
    case "emotions":
      return "\u{2764}\u{FE0F}";
    case "personality":
      return "\u{2728}";
    case "user":
      return "\u{1F464}";
    case "knowledge":
      return "\u{1F4DA}";
    default:
      return "\u{1F4CB}";
  }
}

function renderBar(value: number, max: number, color: string) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return html`
    <div style="
      width: 100%;
      height: 8px;
      border-radius: 4px;
      background: rgba(255,255,255,0.06);
      overflow: hidden;
    ">
      <div style="
        width: ${pct}%;
        height: 100%;
        border-radius: 4px;
        background: ${color};
        transition: width 0.5s ease;
      "></div>
    </div>
  `;
}

function renderStatCard(label: string, value: string | number, icon: string, color: string) {
  return html`
    <div class="card" style="padding: 20px; text-align: center; min-width: 120px;">
      <div style="font-size: 24px; margin-bottom: 8px;">${icon}</div>
      <div style="font-size: 28px; font-weight: 700; color: ${color}; margin-bottom: 4px;">${value}</div>
      <div class="muted" style="font-size: 12px;">${label}</div>
    </div>
  `;
}

function renderOverview(props: LearningProps) {
  const emo = props.emotionalState as EmotionalState | null;
  const up = props.userProfile as UserProfile | null;
  const ts = props.tieredStats as TieredStats | null;

  const hasMemory = Boolean(props.memory?.trim());
  const hasSoul = Boolean(props.soul?.trim());
  const hasEmotions = Boolean(emo);
  const hasUserModel = Boolean(up);
  const hasTiered = Boolean(ts);

  const memoryLines = props.memory?.split("\n").filter((l) => l.trim()).length ?? 0;
  const tieredTotal = ts?.totalMemories ?? 0;

  return html`
    <div style="margin-bottom: 24px;">
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.overview_title")}</h3>
      <div class="grid grid-cols-3" style="gap: 12px; margin-bottom: 24px;">
        ${renderStatCard(t("learning.memory_entries"), String(memoryLines + tieredTotal), "\u{1F9E0}", "var(--accent, #6c8cff)")}
        ${renderStatCard(t("learning.emotions_active"), emo?.activeEmotions?.length ?? 0, "\u{2764}\u{FE0F}", "var(--warning, #f59e0b)")}
        ${renderStatCard(t("learning.user_messages"), up?.interactionStats?.totalMessages ?? "—", "\u{1F4AC}", "var(--success, #34d399)")}
      </div>
    </div>

    <!-- Module status -->
    <div class="card" style="padding: 20px; margin-bottom: 16px;">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;">
        <h4 style="margin: 0; font-size: 15px; font-weight: 600;">${t("learning.modules")}</h4>
        <button
          class="btn btn--sm primary"
          style="font-size: 12px; padding: 6px 16px;"
          ?disabled=${props.initializing === "all"}
          @click=${() => props.onInitAll()}
        >
          ${props.initializing === "all" ? t("learning.generating") : t("learning.init_all")}
        </button>
      </div>
      <div style="display: flex; flex-direction: column; gap: 4px;">
        ${renderModuleRow(t("learning.mod_memory"), hasMemory, t("learning.mod_memory_desc"), "memory", "memory", props)}
        ${renderModuleRow(t("learning.mod_soul"), hasSoul, t("learning.mod_soul_desc"), "personality", "soul", props)}
        ${renderModuleRow(t("learning.mod_emotions"), hasEmotions, t("learning.mod_emotions_desc"), "emotions", "emotions", props)}
        ${renderModuleRow(t("learning.mod_user"), hasUserModel, t("learning.mod_user_desc"), "user", "user", props)}
        ${renderModuleRow(t("learning.mod_tiered"), hasTiered, t("learning.mod_tiered_desc"), "knowledge", "tiered", props)}
      </div>
    </div>

    <!-- Current emotional state summary -->
    ${
      emo
        ? html`
      <div class="card" style="padding: 20px;">
        <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.current_mood")}</h4>
        <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
          ${
            emo.currentEmotion && typeof emo.currentEmotion === "string"
              ? html`<span class="pill" style="background: rgba(245, 158, 11, 0.15); color: #f59e0b; padding: 6px 14px; border-radius: 20px; font-size: 14px;">${emo.currentEmotion}</span>`
              : nothing
          }
          ${(emo.activeEmotions ?? []).map((e) => {
            const label =
              typeof e === "string" ? e : ((e as Record<string, unknown>)?.emotion ?? "");
            return label
              ? html`<span class="pill" style="background: rgba(108, 140, 255, 0.12); color: var(--accent, #6c8cff); padding: 4px 12px; border-radius: 16px; font-size: 13px;">${label}</span>`
              : nothing;
          })}
        </div>
        ${
          emo.mood
            ? html`
          <div style="margin-top: 16px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
            ${renderMoodBar(t("learning.mood_valence"), emo.mood.valence ?? 0, true)}
            ${renderMoodBar(t("learning.mood_stability"), emo.mood.stability ?? 0)}
            ${renderMoodBar(t("learning.mood_energy"), emo.mood.energy ?? 0)}
            ${renderMoodBar(t("learning.mood_warmth"), emo.mood.warmth ?? 0)}
          </div>
        `
            : nothing
        }
      </div>
    `
        : nothing
    }
  `;
}

function renderModuleRow(
  name: string,
  active: boolean,
  desc: string,
  moduleKey: string,
  initKey: string,
  props: LearningProps,
) {
  const section = moduleKey;
  const isInitializing = props.initializing === initKey || props.initializing === "all";

  return html`
    <div
      style="
        display: flex; align-items: center; gap: 12px; padding: 10px 12px;
        border-bottom: 1px solid rgba(255,255,255,0.04);
        border-radius: 8px;
        transition: background 0.15s ease;
      "
    >
      <span style="
        display: inline-block;
        width: 10px; height: 10px;
        border-radius: 50%;
        background: ${active ? "var(--success, #34d399)" : "rgba(255,255,255,0.15)"};
        ${active ? "box-shadow: 0 0 8px rgba(52, 211, 153, 0.4);" : ""}
        flex-shrink: 0;
      "></span>
      <div
        style="flex: 1; min-width: 0; cursor: pointer;"
        @click=${() => props.onSectionChange(section)}
      >
        <div style="font-size: 14px; font-weight: 500;">${name}</div>
        <div class="muted" style="font-size: 12px;">${desc}</div>
      </div>
      <button
        class="btn btn--sm"
        style="
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 8px;
          white-space: nowrap;
          background: ${active ? "rgba(108,140,255,0.1)" : "rgba(52,211,153,0.12)"};
          color: ${active ? "var(--accent, #6c8cff)" : "var(--success, #34d399)"};
          border: 1px solid ${active ? "rgba(108,140,255,0.2)" : "rgba(52,211,153,0.2)"};
        "
        ?disabled=${isInitializing}
        @click=${(e: Event) => {
          e.stopPropagation();
          props.onInitModule(initKey);
        }}
      >
        ${
          isInitializing
            ? t("learning.generating")
            : active
              ? t("learning.regenerate")
              : t("learning.generate")
        }
      </button>
      <span style="font-size: 12px; padding: 2px 10px; border-radius: 10px;
        background: ${active ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.06)"};
        color: ${active ? "var(--success, #34d399)" : "var(--text-muted, #666)"};
      ">${active ? t("learning.active") : t("learning.inactive")}</span>
      <span
        style="font-size: 16px; color: var(--text-muted, #666); margin-left: 4px; cursor: pointer;"
        @click=${() => props.onSectionChange(section)}
      >›</span>
    </div>
  `;
}

function renderMoodBar(label: string, value: number, bipolar = false) {
  const normalized = bipolar ? (value + 1) / 2 : Math.min(1, Math.max(0, value));
  const color =
    value > 0.3
      ? "var(--success, #34d399)"
      : value < -0.3
        ? "var(--danger, #ff6b6b)"
        : "var(--accent, #6c8cff)";
  return html`
    <div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="font-size: 12px; color: var(--text-muted, #999);">${label}</span>
        <span style="font-size: 12px; font-weight: 600;">${(value * 100).toFixed(0)}%</span>
      </div>
      ${renderBar(normalized, 1, color)}
    </div>
  `;
}

function renderEmptyModule(
  props: LearningProps,
  moduleKey: string,
  message: string,
  buttonLabel: string,
) {
  const isInit = props.initializing === moduleKey || props.initializing === "all";
  return html`
    <div class="card" style="padding: 32px; text-align: center;">
      <div class="muted" style="margin-bottom: 16px; font-size: 14px;">${message}</div>
      <button
        class="btn btn--sm primary"
        style="font-size: 13px; padding: 8px 24px;"
        ?disabled=${isInit}
        @click=${() => props.onInitModule(moduleKey)}
      >
        ${isInit ? t("learning.generating") : buttonLabel}
      </button>
    </div>
  `;
}

function formatDate(ms: number): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function slugFromFilename(name: string): string {
  // "2026-02-17-chat-about-xyz.md" → "chat about xyz"
  const withoutExt = name.replace(/\.md$/, "");
  const parts = withoutExt.split("-");
  // Skip date parts (YYYY-MM-DD)
  if (parts.length > 3 && /^\d{4}$/.test(parts[0])) {
    return parts.slice(3).join(" ");
  }
  return withoutExt;
}

function renderMemorySection(props: LearningProps) {
  const ts = props.tieredStats as TieredStats | null;

  return html`
    <div>
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.memory_title")}</h3>

      ${
        ts
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.tiered_memory")}</h4>
          <div class="grid grid-cols-3" style="gap: 12px; margin-bottom: 16px;">
            ${renderStatCard(t("learning.short_term"), ts.shortTermCount, "\u{26A1}", "var(--warning, #f59e0b)")}
            ${renderStatCard(t("learning.long_term"), ts.longTermCount, "\u{1F4BE}", "var(--accent, #6c8cff)")}
            ${renderStatCard(t("learning.entities"), ts.entityCount, "\u{1F517}", "var(--success, #34d399)")}
          </div>

          ${
            ts.entityCount > 0
              ? html`
            <h5 style="margin: 16px 0 8px; font-size: 13px; font-weight: 600; color: var(--text-muted, #999);">${t("learning.entity_graph")}</h5>
            <div style="display: flex; flex-wrap: wrap; gap: 6px;">
              ${Object.keys(ts.entities).map(
                (entity) => html`
                <span style="
                  padding: 4px 12px;
                  border-radius: 14px;
                  background: rgba(108,140,255,0.1);
                  color: var(--accent, #6c8cff);
                  font-size: 12px;
                  border: 1px solid rgba(108,140,255,0.15);
                ">${entity}</span>
              `,
              )}
            </div>
          `
              : nothing
          }

          ${
            ts.shortTerm.length > 0
              ? html`
            <h5 style="margin: 16px 0 8px; font-size: 13px; font-weight: 600; color: var(--text-muted, #999);">${t("learning.recent_memories")}</h5>
            <div style="max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px;">
              ${(ts.shortTerm as Array<{ content?: unknown; type?: string; createdAt?: string }>)
                .slice(-10)
                .reverse()
                .map(
                  (m) => html`
                <div style="padding: 10px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); font-size: 13px;">
                  ${m.type ? html`<span style="font-size: 11px; color: var(--accent, #6c8cff); text-transform: uppercase; font-weight: 600; margin-right: 8px;">${m.type}</span>` : nothing}
                  ${typeof m.content === "string" ? m.content : m.content ? JSON.stringify(m.content) : "—"}
                </div>
              `,
                )}
            </div>
          `
              : nothing
          }
        </div>
      `
          : nothing
      }

      ${
        props.memory
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.memory_file")}</h4>
          <pre style="
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 13px;
            line-height: 1.6;
            max-height: 400px;
            overflow-y: auto;
            padding: 16px;
            border-radius: 10px;
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.05);
            margin: 0;
          ">${props.memory}</pre>
        </div>
      `
          : renderEmptyModule(
              props,
              "memory",
              t("learning.no_memory"),
              t("learning.generate_memory"),
            )
      }

      <!-- Memory History Timeline -->
      <div class="card" style="padding: 20px;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;">
          <h4 style="margin: 0; font-size: 15px; font-weight: 600;">${t("learning.memory_history")}</h4>
          <button
            class="btn btn--sm"
            style="font-size: 11px; padding: 4px 12px;"
            ?disabled=${props.memoryFilesLoading}
            @click=${() => props.onLoadMemoryFiles()}
          >
            ${props.memoryFilesLoading ? t("learning.loading") : t("learning.load_history")}
          </button>
        </div>

        ${
          props.memoryFiles.length > 0
            ? html`
          <div style="display: flex; flex-direction: column; gap: 2px; max-height: 600px; overflow-y: auto;">
            ${props.memoryFiles.map(
              (file) => html`
              <div>
                <div
                  style="
                    display: flex; align-items: center; gap: 10px; padding: 10px 14px;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    background: ${props.selectedMemoryFile === file.path ? "rgba(108,140,255,0.1)" : "rgba(255,255,255,0.02)"};
                    border: 1px solid ${props.selectedMemoryFile === file.path ? "rgba(108,140,255,0.2)" : "rgba(255,255,255,0.04)"};
                  "
                  @click=${() => props.onSelectMemoryFile(file.path)}
                >
                  <span style="
                    display: inline-block; width: 8px; height: 8px; border-radius: 50%;
                    background: var(--accent, #6c8cff); flex-shrink: 0;
                    box-shadow: 0 0 6px rgba(108, 140, 255, 0.3);
                  "></span>
                  <div style="flex: 1; min-width: 0;">
                    <div style="font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                      ${slugFromFilename(file.name) || file.name}
                    </div>
                    <div class="muted" style="font-size: 11px;">${file.name}</div>
                  </div>
                  <div style="text-align: right; flex-shrink: 0;">
                    <div class="muted" style="font-size: 11px;">${formatDate(file.modifiedAt)}</div>
                    <div class="muted" style="font-size: 10px;">${formatFileSize(file.size)}</div>
                  </div>
                  <span style="font-size: 14px; color: var(--text-muted, #666); transform: rotate(${props.selectedMemoryFile === file.path ? "90deg" : "0deg"}); transition: transform 0.2s;">›</span>
                </div>

                ${
                  props.selectedMemoryFile === file.path
                    ? html`
                  <div style="
                    margin: 4px 0 8px 18px;
                    padding: 14px 16px;
                    border-radius: 10px;
                    background: rgba(0,0,0,0.25);
                    border: 1px solid rgba(255,255,255,0.05);
                    border-left: 3px solid var(--accent, #6c8cff);
                  ">
                    ${
                      props.selectedMemoryContent === null
                        ? html`<div class="muted" style="font-size: 13px;">${t("learning.loading")}...</div>`
                        : html`<pre style="
                            white-space: pre-wrap;
                            word-break: break-word;
                            font-size: 12px;
                            line-height: 1.6;
                            max-height: 400px;
                            overflow-y: auto;
                            margin: 0;
                            color: var(--text, #eee);
                          ">${props.selectedMemoryContent}</pre>`
                    }
                  </div>
                `
                    : nothing
                }
              </div>
            `,
            )}
          </div>
        `
            : html`
          <div class="muted" style="text-align: center; font-size: 13px; padding: 16px 0;">
            ${t("learning.no_history")}
          </div>
        `
        }
      </div>
    </div>
  `;
}

function renderEmotionsSection(props: LearningProps) {
  const emo = props.emotionalState as EmotionalState | null;
  if (!emo) {
    return renderEmptyModule(
      props,
      "emotions",
      t("learning.no_emotions"),
      t("learning.generate_emotions"),
    );
  }

  const vec = emo.emotionVector;
  const loyalty = emo.loyalty;

  return html`
    <div>
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.emotions_title")}</h3>

      <!-- Emotion vector -->
      ${
        vec
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 16px; font-size: 15px; font-weight: 600;">${t("learning.emotion_vector")}</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
            ${renderVectorBar(t("learning.vec_valence"), vec.valence ?? 0, "#6c8cff", true)}
            ${renderVectorBar(t("learning.vec_arousal"), vec.arousal ?? 0, "#f59e0b")}
            ${renderVectorBar(t("learning.vec_dominance"), vec.dominance ?? 0, "#34d399")}
            ${renderVectorBar(t("learning.vec_attachment"), vec.attachment ?? 0, "#ec4899")}
            ${renderVectorBar(t("learning.vec_desire"), vec.desire ?? 0, "#a78bfa")}
            ${renderVectorBar(t("learning.vec_confusion"), vec.confusion ?? 0, "#94a3b8")}
          </div>
        </div>
      `
          : nothing
      }

      <!-- Active emotions -->
      ${
        emo.activeEmotions?.length
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.active_emotions")}</h4>
          <div style="display: flex; flex-wrap: wrap; gap: 8px;">
            ${emo.activeEmotions.map((e) => {
              const label =
                typeof e === "string" ? e : ((e as Record<string, unknown>)?.emotion ?? "");
              return label
                ? html`
              <span style="
                padding: 8px 18px;
                border-radius: 20px;
                background: linear-gradient(135deg, rgba(108,140,255,0.15), rgba(139,92,246,0.15));
                color: var(--text, #eee);
                font-size: 14px;
                font-weight: 500;
                border: 1px solid rgba(108,140,255,0.2);
              ">${label}</span>
            `
                : nothing;
            })}
          </div>
        </div>
      `
          : nothing
      }

      <!-- Loyalty -->
      ${
        loyalty
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 16px; font-size: 15px; font-weight: 600;">${t("learning.loyalty")}</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
            ${renderVectorBar(t("learning.loyalty_level"), loyalty.loyaltyLevel ?? 0, "#ec4899")}
            ${renderVectorBar(t("learning.bond_strength"), loyalty.bondStrength ?? 0, "#f59e0b")}
            ${renderVectorBar(t("learning.protective"), loyalty.protectiveInstinct ?? 0, "#34d399")}
          </div>
          ${
            loyalty.devotionStyle
              ? html`<div style="margin-top: 12px; font-size: 13px; color: var(--text-muted, #999);">${t("learning.devotion_style")}: <span style="color: var(--text, #eee); font-weight: 500;">${loyalty.devotionStyle}</span></div>`
              : nothing
          }
        </div>
      `
          : nothing
      }

      <!-- Emotional memories -->
      ${
        emo.memories?.length
          ? html`
        <div class="card" style="padding: 20px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.emotional_memories")} (${emo.memories.length})</h4>
          <div style="max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px;">
            ${(emo.memories as Array<Record<string, unknown>>)
              .slice(-15)
              .reverse()
              .map((raw) => {
                const emotion =
                  (raw.emotion as string) ?? (raw as any).emotionalSnapshot?.dominantEmotion ?? "";
                const summary =
                  typeof raw.summary === "string"
                    ? raw.summary
                    : typeof raw.event === "string"
                      ? (raw.event as string)
                      : "";
                return html`
              <div style="padding: 10px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); font-size: 13px;">
                ${emotion ? html`<span style="color: var(--warning, #f59e0b); font-weight: 600; margin-right: 6px;">${emotion}</span>` : nothing}
                ${summary || "—"}
              </div>
            `;
              })}
          </div>
        </div>
      `
          : nothing
      }
    </div>
  `;
}

function renderVectorBar(label: string, value: number, color: string, bipolar = false) {
  const normalized = bipolar
    ? Math.min(1, Math.max(0, (value + 1) / 2))
    : Math.min(1, Math.max(0, value));
  return html`
    <div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="font-size: 12px; color: var(--text-muted, #999);">${label}</span>
        <span style="font-size: 12px; font-weight: 600;">${value.toFixed(2)}</span>
      </div>
      ${renderBar(normalized, 1, color)}
    </div>
  `;
}

function renderPersonalitySection(props: LearningProps) {
  return html`
    <div>
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.personality_title")}</h3>

      ${
        props.soul
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.soul_file")}</h4>
          <pre style="
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 13px;
            line-height: 1.6;
            max-height: 400px;
            overflow-y: auto;
            padding: 16px;
            border-radius: 10px;
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.05);
            margin: 0;
          ">${props.soul}</pre>
        </div>
      `
          : renderEmptyModule(props, "soul", t("learning.no_soul"), t("learning.generate_soul"))
      }

      ${
        props.identity
          ? html`
        <div class="card" style="padding: 20px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.identity_file")}</h4>
          <pre style="
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 13px;
            line-height: 1.6;
            max-height: 300px;
            overflow-y: auto;
            padding: 16px;
            border-radius: 10px;
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.05);
            margin: 0;
          ">${props.identity}</pre>
        </div>
      `
          : nothing
      }
    </div>
  `;
}

function renderUserSection(props: LearningProps) {
  const up = props.userProfile as UserProfile | null;

  return html`
    <div>
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.user_title")}</h3>

      ${
        up
          ? html`
        <div class="card" style="padding: 20px; margin-bottom: 16px;">
          <h4 style="margin: 0 0 16px; font-size: 15px; font-weight: 600;">${t("learning.user_profile")}</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
            ${renderProfileField(t("learning.comm_style"), up.communicationStyle ?? "—")}
            ${renderProfileField(t("learning.expertise"), up.expertiseLevel ?? "—")}
            ${renderProfileField(t("learning.response_pref"), up.responsePreference ?? "—")}
            ${renderProfileField(t("learning.mental_state"), up.mentalState?.emotion ?? "—")}
          </div>
        </div>

        ${
          up.topInterests?.length
            ? html`
          <div class="card" style="padding: 20px; margin-bottom: 16px;">
            <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.interests")}</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 6px;">
              ${up.topInterests.map(
                (i) => html`
                <span style="
                  padding: 6px 14px;
                  border-radius: 16px;
                  background: rgba(52,211,153,0.1);
                  color: var(--success, #34d399);
                  font-size: 13px;
                  border: 1px solid rgba(52,211,153,0.15);
                ">${i}</span>
              `,
              )}
            </div>
          </div>
        `
            : nothing
        }

        ${
          up.interactionStats
            ? html`
          <div class="card" style="padding: 20px;">
            <h4 style="margin: 0 0 16px; font-size: 15px; font-weight: 600;">${t("learning.interaction_stats")}</h4>
            <div class="grid grid-cols-2" style="gap: 12px;">
              ${renderStatCard(t("learning.stat_messages"), up.interactionStats.totalMessages ?? 0, "\u{1F4AC}", "var(--accent, #6c8cff)")}
              ${renderStatCard(t("learning.stat_questions"), up.interactionStats.questions ?? 0, "\u{2753}", "var(--warning, #f59e0b)")}
              ${renderStatCard(t("learning.stat_commands"), up.interactionStats.commands ?? 0, "\u{2328}\u{FE0F}", "var(--success, #34d399)")}
              ${renderStatCard(t("learning.stat_code"), up.interactionStats.codeSnippets ?? 0, "\u{1F4BB}", "var(--danger, #ff6b6b)")}
            </div>
          </div>
        `
            : nothing
        }
      `
          : nothing
      }

      ${
        props.user
          ? html`
        <div class="card" style="padding: 20px; margin-top: 16px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.user_file")}</h4>
          <pre style="
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 13px;
            line-height: 1.6;
            max-height: 300px;
            overflow-y: auto;
            padding: 16px;
            border-radius: 10px;
            background: rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.05);
            margin: 0;
          ">${props.user}</pre>
        </div>
      `
          : renderEmptyModule(props, "user", t("learning.no_user"), t("learning.generate_user"))
      }
    </div>
  `;
}

function renderProfileField(label: string, value: string) {
  return html`
    <div style="padding: 12px 16px; border-radius: 12px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05);">
      <div class="muted" style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">${label}</div>
      <div style="font-size: 14px; font-weight: 500; text-transform: capitalize;">${value}</div>
    </div>
  `;
}

function renderKnowledgeSection(props: LearningProps) {
  const subTab = props.knowledgeSubTab ?? "sources";

  return html`
    <div>
      <!-- Sub-tab switcher -->
      <div class="row" style="gap: 0; margin-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.08);">
        <button
          class="sub-tab ${subTab === "sources" ? "active" : ""}"
          @click=${() => props.onKnowledgeSubTabChange("sources")}
        >
          📡 ${t("ks.tab_sources")}
        </button>
        <button
          class="sub-tab ${subTab === "collected" ? "active" : ""}"
          @click=${() => props.onKnowledgeSubTabChange("collected")}
        >
          📚 ${t("ks.tab_collected")}
        </button>
      </div>

      ${
        subTab === "sources"
          ? renderKnowledgeSources({
              loading: props.knowledgeSourcesLoading,
              error: props.knowledgeSourcesError,
              sources: props.knowledgeSources,
              editingSource: props.knowledgeSourceEditing,
              formOpen: props.knowledgeSourceFormOpen,
              collecting: props.knowledgeSourceCollecting,
              testing: false,
              testResult: null,
              onRefresh: props.onKnowledgeSourceRefresh,
              onAdd: props.onKnowledgeSourceAdd,
              onEdit: props.onKnowledgeSourceEdit,
              onDelete: props.onKnowledgeSourceDelete,
              onCollect: props.onKnowledgeSourceCollect,
              onToggle: props.onKnowledgeSourceToggle,
              onFormClose: props.onKnowledgeSourceFormClose,
              onFormSave: props.onKnowledgeSourceFormSave,
              onTestConnection: () => {},
            } as KnowledgeSourcesViewProps)
          : renderCollectedKnowledge(props)
      }
    </div>
  `;
}

function renderCollectedKnowledge(props: LearningProps) {
  const ts = props.tieredStats as TieredStats | null;
  const longTerm = (ts?.longTerm ?? []) as Array<{
    content?: string;
    type?: string;
    tags?: string[];
    accessCount?: number;
  }>;

  return html`
    <div>
      <h3 style="margin: 0 0 16px; font-size: 18px; font-weight: 600;">${t("learning.knowledge_title")}</h3>

      ${
        longTerm.length > 0
          ? html`
        <div class="card" style="padding: 20px;">
          <h4 style="margin: 0 0 12px; font-size: 15px; font-weight: 600;">${t("learning.long_term_knowledge")} (${longTerm.length})</h4>
          <div style="max-height: 500px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px;">
            ${longTerm.slice(0, 50).map(
              (m) => html`
              <div style="padding: 12px 16px; border-radius: 12px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05);">
                <div style="font-size: 13px; line-height: 1.5; margin-bottom: 6px;">${typeof m.content === "string" ? m.content : m.content ? JSON.stringify(m.content) : "—"}</div>
                <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
                  ${m.type ? html`<span style="font-size: 11px; padding: 2px 8px; border-radius: 8px; background: rgba(108,140,255,0.1); color: var(--accent, #6c8cff);">${m.type}</span>` : nothing}
                  ${(m.tags ?? []).map((tag) => html`<span style="font-size: 11px; padding: 2px 8px; border-radius: 8px; background: rgba(255,255,255,0.06); color: var(--text-muted, #999);">${tag}</span>`)}
                  ${m.accessCount ? html`<span class="muted" style="font-size: 11px; margin-left: auto;">x${m.accessCount}</span>` : nothing}
                </div>
              </div>
            `,
            )}
          </div>
        </div>
      `
          : renderEmptyModule(
              props,
              "tiered",
              t("learning.no_knowledge"),
              t("learning.generate_knowledge"),
            )
      }
    </div>
  `;
}

export function renderLearning(props: LearningProps) {
  if (!props.connected) {
    return html`<section class="card muted" style="padding: 24px; text-align: center;">${t("learning.disconnected")}</section>`;
  }

  if (props.loading) {
    return html`<section class="card muted" style="padding: 24px; text-align: center;">${t("learning.loading")}</section>`;
  }

  if (props.error) {
    return html`<section class="card" style="padding: 24px;"><div class="callout danger">${props.error}</div></section>`;
  }

  const section = props.activeSection || "overview";

  return html`
    <section style="display: flex; gap: 20px; min-height: 0;">
      <!-- Sidebar -->
      <div style="
        width: 200px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      ">
        ${SECTIONS.map(
          (s) => html`
          <button
            class="btn ${section === s ? "btn--primary" : ""}"
            style="
              text-align: left;
              padding: 10px 14px;
              border-radius: 10px;
              font-size: 13px;
              display: flex;
              align-items: center;
              gap: 8px;
              ${section === s ? "" : "background: transparent; border: none; color: var(--text-muted, #999);"}
            "
            @click=${() => props.onSectionChange(s)}
          >
            <span>${sectionIcon(s)}</span>
            <span>${t(`learning.sec_${s}` as any)}</span>
          </button>
        `,
        )}

      </div>

      <!-- Content -->
      <div style="flex: 1; min-width: 0; overflow-y: auto;">
        ${section === "overview" ? renderOverview(props) : nothing}
        ${section === "memory" ? renderMemorySection(props) : nothing}
        ${section === "emotions" ? renderEmotionsSection(props) : nothing}
        ${section === "personality" ? renderPersonalitySection(props) : nothing}
        ${section === "user" ? renderUserSection(props) : nothing}
        ${section === "knowledge" ? renderKnowledgeSection(props) : nothing}
      </div>
    </section>
  `;
}
